/**
 * Insight Generator
 * Analyzes user health data to identify patterns and generate personalized insights
 */

/**
 * Generate mock user data for the last 7 days
 * In production, this would come from backend API
 */
export const generateMockData = () => {
  const today = new Date();
  const data = [];

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    // Generate realistic mock data with patterns
    const sleepWell = Math.random() > 0.3; // 70% sleep well
    const drankWater = Math.random() > 0.4; // 60% hydrated
    const moved = Math.random() > 0.35; // 65% active
    
    // Mood correlates with sleep and hydration
    let mood = 'okay';
    if (sleepWell && drankWater) {
      mood = Math.random() > 0.3 ? 'good' : 'okay';
    } else if (!sleepWell) {
      mood = Math.random() > 0.6 ? 'tired' : 'okay';
    }
    
    // Symptoms correlate with habits
    let symptoms = [];
    if (!drankWater && Math.random() > 0.5) {
      symptoms.push('headache');
    }
    if (!sleepWell && Math.random() > 0.6) {
      symptoms.push('tired');
    }
    if (!moved && Math.random() > 0.7) {
      symptoms.push('stressed');
    }

    data.push({
      date: date.toISOString().split('T')[0],
      sleep: sleepWell,
      hydration: drankWater,
      activity: moved,
      mood: mood,
      symptoms: symptoms
    });
  }

  return data;
};

/**
 * Analyze patterns in user data
 */
export const analyzePatterns = (weekData) => {
  const insights = [];
  
  // Pattern 1: Sleep → Mood correlation
  const lowSleepDays = weekData.filter(d => !d.sleep);
  const lowSleepTiredDays = lowSleepDays.filter(d => d.mood === 'tired');
  
  if (lowSleepDays.length >= 2 && lowSleepTiredDays.length >= 2) {
    insights.push({
      type: 'pattern',
      category: 'sleep',
      severity: 'medium',
      icon: '😴',
      text: 'You feel tired more often on low sleep days',
      detail: `${lowSleepTiredDays.length} out of ${lowSleepDays.length} low-sleep days`
    });
  }
  
  // Pattern 2: Hydration → Headaches correlation
  const lowHydrationDays = weekData.filter(d => !d.hydration);
  const lowHydrationHeadacheDays = lowHydrationDays.filter(d => 
    d.symptoms.includes('headache')
  );
  
  if (lowHydrationDays.length >= 2 && lowHydrationHeadacheDays.length >= 1) {
    insights.push({
      type: 'pattern',
      category: 'hydration',
      severity: 'medium',
      icon: '💧',
      text: 'You report headaches more on low hydration days',
      detail: `${lowHydrationHeadacheDays.length} headaches on ${lowHydrationDays.length} low-hydration days`
    });
  }
  
  // Pattern 3: Activity → Stress correlation
  const lowActivityDays = weekData.filter(d => !d.activity);
  const lowActivityStressDays = lowActivityDays.filter(d => 
    d.symptoms.includes('stressed') || d.mood === 'stressed'
  );
  
  if (lowActivityDays.length >= 2 && lowActivityStressDays.length >= 1) {
    insights.push({
      type: 'pattern',
      category: 'activity',
      severity: 'low',
      icon: '🏃',
      text: 'Stress tends to be higher on days without movement',
      detail: `${lowActivityStressDays.length} stressed days without activity`
    });
  }
  
  // Pattern 4: Good habits → Good mood
  const goodHabitDays = weekData.filter(d => d.sleep && d.hydration && d.activity);
  const goodHabitGoodMoodDays = goodHabitDays.filter(d => d.mood === 'good');
  
  if (goodHabitDays.length >= 2 && goodHabitGoodMoodDays.length >= 1) {
    insights.push({
      type: 'positive',
      category: 'overall',
      severity: 'positive',
      icon: '✨',
      text: 'You feel better when you maintain all healthy habits',
      detail: `${goodHabitGoodMoodDays.length} out of ${goodHabitDays.length} complete habit days felt good`
    });
  }

  return insights;
};

/**
 * Generate weekly summary
 */
export const generateWeeklySummary = (weekData) => {
  const sleepCount = weekData.filter(d => d.sleep).length;
  const hydrationCount = weekData.filter(d => d.hydration).length;
  const activityCount = weekData.filter(d => d.activity).length;
  const goodMoodCount = weekData.filter(d => d.mood === 'good').length;

  const improvements = [];
  const needsAttention = [];

  // Check improvements (>70% = 5+ out of 7 days)
  if (sleepCount >= 5) {
    improvements.push({
      icon: '🌙',
      text: 'Sleep consistency',
      detail: `${sleepCount} out of 7 days`,
      color: 'text-blue-600'
    });
  }
  
  if (hydrationCount >= 5) {
    improvements.push({
      icon: '💧',
      text: 'Staying hydrated',
      detail: `${hydrationCount} out of 7 days`,
      color: 'text-blue-600'
    });
  }
  
  if (activityCount >= 5) {
    improvements.push({
      icon: '🏃',
      text: 'Regular movement',
      detail: `${activityCount} out of 7 days`,
      color: 'text-green-600'
    });
  }

  if (goodMoodCount >= 4) {
    improvements.push({
      icon: '😊',
      text: 'Positive mood',
      detail: `${goodMoodCount} out of 7 days`,
      color: 'text-green-600'
    });
  }

  // Check areas needing attention (<50% = <3 out of 7 days)
  if (sleepCount < 4) {
    needsAttention.push({
      icon: '😴',
      text: 'Sleep quality',
      detail: `Only ${sleepCount} out of 7 days`,
      color: 'text-orange-600'
    });
  }
  
  if (hydrationCount < 4) {
    needsAttention.push({
      icon: '💧',
      text: 'Water intake',
      detail: `Only ${hydrationCount} out of 7 days`,
      color: 'text-orange-600'
    });
  }
  
  if (activityCount < 4) {
    needsAttention.push({
      icon: '🏃',
      text: 'Physical activity',
      detail: `Only ${activityCount} out of 7 days`,
      color: 'text-orange-600'
    });
  }

  return { improvements, needsAttention };
};

/**
 * Generate actionable suggestions based on patterns and summary
 */
export const generateSuggestions = (insights, weekData) => {
  const suggestions = [];
  
  const sleepCount = weekData.filter(d => d.sleep).length;
  const hydrationCount = weekData.filter(d => d.hydration).length;
  const activityCount = weekData.filter(d => d.activity).length;

  // Sleep suggestions
  if (sleepCount < 5) {
    suggestions.push({
      icon: '🌙',
      title: 'Improve your sleep',
      action: 'Try going to bed 30 minutes earlier',
      why: 'Better sleep can improve your energy and mood',
      priority: sleepCount < 3 ? 'high' : 'medium'
    });
  }

  // Hydration suggestions
  if (hydrationCount < 5) {
    suggestions.push({
      icon: '💧',
      title: 'Increase water intake',
      action: 'Keep a water bottle nearby throughout the day',
      why: 'Proper hydration can reduce headaches and improve focus',
      priority: hydrationCount < 3 ? 'high' : 'medium'
    });
  }

  // Activity suggestions
  if (activityCount < 5) {
    suggestions.push({
      icon: '🚶',
      title: 'Add more movement',
      action: 'Take a 10-minute walk when you feel stressed',
      why: 'Regular movement helps manage stress and improves wellbeing',
      priority: activityCount < 3 ? 'medium' : 'low'
    });
  }

  // Pattern-based suggestions
  const stressPattern = insights.find(i => i.category === 'activity' && i.text.includes('stress'));
  if (stressPattern) {
    suggestions.push({
      icon: '🧘',
      title: 'Manage stress better',
      action: 'Try 5 minutes of deep breathing when feeling stressed',
      why: 'Movement and breathing exercises can help reduce stress',
      priority: 'medium'
    });
  }

  const headachePattern = insights.find(i => i.category === 'hydration');
  if (headachePattern) {
    suggestions.push({
      icon: '💧',
      title: 'Prevent headaches',
      action: 'Drink 2 glasses of water first thing in the morning',
      why: 'Morning hydration can help prevent headaches later',
      priority: 'high'
    });
  }

  // Sort by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  suggestions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return suggestions.slice(0, 3); // Return top 3 suggestions
};

/**
 * Main function to generate all insights
 */
export const generateProgressInsights = () => {
  const weekData = generateMockData();
  const patterns = analyzePatterns(weekData);
  const summary = generateWeeklySummary(weekData);
  const suggestions = generateSuggestions(patterns, weekData);

  return {
    weekData,
    patterns,
    summary,
    suggestions
  };
};
